import { useEffect, useRef, useState } from "react";
import { ITable, IHall, IObject } from "../App";

type ITableHighlight = {
    tableId: number,
    x: number, 
    y: number, 
    w: number,
    h: number,
};


type TableCanvasProps = {
    selectedTable?: ITable;
    selectedHall: IHall;
    onSelectTable?: (newSelectedTable: ITable) => void;
    object: IObject;
    disabled: boolean;
};

function pointInsideHighlight(x: number, y: number, highlight: ITableHighlight) {
    return x >= highlight.x && highlight.x + highlight.w >= x
        && y >= highlight.y && y <= highlight.y + highlight.h;
}

export function TableCanvas(props: TableCanvasProps) {
    const urlPrefix = `${process.env.REACT_APP_API_PREFIX}/themes/mdosc-materialize-redesign/assets/reservations/`;

    let canvas = useRef<HTMLCanvasElement>(null);

    let [sourceImage, setSourceImage] = useState<HTMLImageElement>(new Image());
    let [sourceImageLoaded, setSourceImageLoaded] = useState<boolean>(false);
    let [highlightsLoaded, setHighlightsLoaded] = useState<boolean>(false);
    let [highlights, setHighlights] = useState<ITableHighlight[]>([]);

    function drawImage() {
        if(canvas.current && sourceImageLoaded && highlightsLoaded) {
            let ctx = canvas.current.getContext("2d");
            if(!ctx) {
                return ;
            }

            
            
            let imageWidth = sourceImage.width;
            let imageHeight = sourceImage.height;

            canvas.current.width = imageWidth;
            canvas.current.height = imageHeight;
            
            ctx.clearRect(0, 0, imageWidth, imageHeight);
            ctx.drawImage(sourceImage, 0, 0, imageWidth, imageHeight);

            for (const highlight of highlights) {
                drawHighlight(highlight, props.selectedTable?.id === highlight.tableId);                
            }
        }
    }

    function drawHighlight(highlight: ITableHighlight, isSelected: boolean) {
        if(canvas.current && sourceImageLoaded && highlightsLoaded) {
            let ctx = canvas.current.getContext("2d");
            if(!ctx) {
                return ;
            }            

            let strokeWidth: number = Math.ceil(0.01 * Math.min(
                canvas.current.width, 
                canvas.current.height
            ));
            
            if(isSelected) {
                ctx.save();
                ctx.fillStyle = '#ffff0096';     
                ctx.lineWidth = strokeWidth;    
                ctx.strokeStyle = '#000000ff';       

                ctx.beginPath();
                ctx.rect(highlight.x, highlight.y, highlight.w, highlight.h);
                ctx.fill();
                ctx.stroke();
                ctx.closePath();
                ctx.restore();
            } else {
                ctx.save();
                ctx.fillStyle = '#ffffff01';     
                ctx.lineWidth = strokeWidth;    
                ctx.strokeStyle = '#6ed86b83';       

                ctx.beginPath();
                ctx.rect(highlight.x, highlight.y, highlight.w, highlight.h);
                ctx.fill();
                ctx.stroke();
                ctx.closePath();
                ctx.restore();
            }            
        }
    }

    useEffect( () => {
        setSourceImageLoaded(false);
        setHighlightsLoaded(false);

        setSourceImage( () => {
            let img = new Image();
            img.onload = () => setSourceImageLoaded(true);
            img.src = urlPrefix + `object-${props.object.id}_hall-${props.selectedHall.id}.png`;
            return img;
        });

        
        let url: string = urlPrefix + `object-${props.object.id}_hall-${props.selectedHall.id}.json`;

        fetch(url)
            .then(response => response.json())
            .then( (response: ITableHighlight[]) => {
                setHighlights(response);
                setHighlightsLoaded(true);
            }).catch( (reason) => console.error(reason));

        
    }, [props.object, props.selectedHall, urlPrefix]);

    useEffect( () => {                  
        if(sourceImageLoaded && highlightsLoaded) {
            if(canvas.current) {
                canvas.current.style.display = 'block';
                drawImage();
            }
        } else {
            if(canvas.current) {
                canvas.current.style.display = 'none';                
            }
        }     
    }, [highlightsLoaded, sourceImageLoaded]);

    useEffect( () => {
        if(props.selectedTable) {
            drawImage();
        }
    }, [props.selectedTable]);    

    return (
        <canvas ref={canvas} style={{ width: '100%', display: 'none' }} onMouseDown={(event) => {
            if(highlightsLoaded) {
                let canvas = event.currentTarget;

                const canvasBoundingRects = canvas.getBoundingClientRect();

                const x = (event.clientX - canvasBoundingRects.left) / parseInt(window.getComputedStyle(canvas).width) * canvas.width;
                const y = (event.clientY - canvasBoundingRects.top) / parseInt(window.getComputedStyle(canvas).height) * canvas.height;

                for (const highlight of highlights) {
                    if(pointInsideHighlight(x, y, highlight)) {
                            const table = props.selectedHall.tables.find( (value) => {
                                return value.id === highlight.tableId;
                            });

                            if(table && props.onSelectTable) {
                                props.onSelectTable(table);                                
                            }                            
                        }
                }
            }
            
        }}></canvas>
    );
}